import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import examService from "../../services/examService";
import { queryKeys } from "./queryKeys";

export const useExams = (filters = {}) => {
  return useQuery({
    queryKey: queryKeys.exams.list(filters),
    queryFn: () => examService.getExams(filters),
    staleTime: 5 * 60 * 1000,
  });
};

export const useExam = (id) => {
  return useQuery({
    queryKey: queryKeys.exams.detail(id),
    queryFn: () => examService.getExamById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useExamResults = (filters = {}) => {
  return useQuery({
    queryKey: [...queryKeys.exams.all, "results", filters],
    queryFn: () => examService.getExamResults(filters),
    staleTime: 5 * 60 * 1000,
  });
};

export const useStudentResults = (studentId, filters = {}) => {
  return useQuery({
    queryKey: queryKeys.exams.results(studentId),
    queryFn: () => examService.getStudentResults(studentId, filters),
    enabled: !!studentId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useExamStatistics = (examId) => {
  return useQuery({
    queryKey: [...queryKeys.exams.detail(examId), "statistics"],
    queryFn: () => examService.getExamStatistics(examId),
    enabled: !!examId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateExam = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (examData) => examService.createExam(examData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.exams.lists(),
      });
    },
  });
};

export const useUpdateExam = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, examData }) =>
      examService.updateExam(id, examData),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.exams.detail(variables.id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.exams.lists(),
      });
    },
  });
};

export const useDeleteExam = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => examService.deleteExam(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.exams.lists(),
      });
    },
  });
};

export const useSubmitScores = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ examId, scores }) =>
      examService.submitScores(examId, scores),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.exams.detail(variables.examId),
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.exams.all, "results"],
      });
    },
  });
};

export const useUpdateStudentScore = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ examId, studentId, data }) =>
      examService.updateStudentScore(examId, studentId, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.exams.detail(variables.examId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.exams.results(variables.studentId),
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.exams.all, "results"],
      });
    },
  });
};

export const usePublishResults = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (examId) => examService.publishResults(examId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.exams.detail(variables),
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.exams.all, "results"],
      });
    },
  });
};

export const useUnpublishResults = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (examId) => examService.unpublishResults(examId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.exams.detail(variables),
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.exams.all, "results"],
      });
    },
  });
};

export const useDownloadExamReport = () => {
  return useMutation({
    mutationFn: (examId) => examService.downloadExamReport(examId),
  });
};

export const useDownloadResultCard = () => {
  return useMutation({
    mutationFn: ({ studentId, params }) =>
      examService.downloadResultCard(studentId, params),
  });
};

